import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { HashRouter, Routes, Route, Link, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { 
  Users, Home, Search, Bell, Briefcase, Heart, 
  Globe, LogOut, Settings, BookOpen, Calendar, MessageCircle, Shield, User as UserIcon, Quote, Mail, Lock, AlertTriangle, Church as ChurchIcon, LayoutDashboard, Check, X, Menu, Save, LogIn, HelpCircle
} from 'lucide-react';
import { User, Church, Post, UserRole, ChurchEvent, Message, PrayerGroup, Comment, Cause, Sermon } from './types';
import { supabase } from './services/supabaseClient';
import SEO from './components/SEO';
import { translations } from './translations';

// Components
import Feed from './components/Feed';
import ChurchPage from './components/ChurchPage';
import SermonBuilder from './components/SermonBuilder';
import Volunteering from './components/Volunteering';
import EventsPage from './components/EventsPage';
import Chat from './components/Chat';
import Profile from './components/Profile';
import SettingsPage from './components/Settings';
import UserManagement from './components/UserManagement';
import ChurchRegistration from './components/ChurchRegistration';
import ChurchDashboard from './components/ChurchDashboard';
import SearchResults from './components/SearchResults';
import UserGuide from './components/UserGuide';
import Contact from './components/Contact';

// --- Context & State Interfaces ---

interface AppState {
  currentUser: User | null;
  allUsers: User[];
  updateUser: (user: User) => Promise<void>;
  updateUserRole: (userId: string, newRole: UserRole) => void;
  deleteUser: (userId: string) => void;
  
  // Connection Management
  incomingRequests: string[]; 
  outgoingRequests: string[]; 
  sendConnectionRequest: (targetUserId: string) => void;
  acceptConnectionRequest: (requesterId: string) => void;
  removeConnection: (targetUserId: string) => void; 
  toggleConnection: (targetUserId: string) => void;

  login: () => void;
  logout: () => void;
  switchRole: (role: UserRole) => void;
  churches: Church[];
  updateChurch: (church: Church) => Promise<void>;
  posts: Post[];
  addPost: (post: Post) => void;
  removePost: (postId: string) => void;
  toggleLike: (postId: string) => void;
  addComment: (postId: string, content: string) => void;
  events: ChurchEvent[];
  addEvent: (event: ChurchEvent) => void;
  updateEvent: (event: ChurchEvent) => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
  
  // Event Registration
  joinedEventIds: string[]; // List of events user has RSVP'd to
  registerForEvent: (eventId: string) => Promise<{ success: boolean; message: string }>;
  
  prayerGroups: PrayerGroup[];
  addPrayerGroup: (group: PrayerGroup) => void;
  joinPrayerGroup: (groupId: string) => void;
  leavePrayerGroup: (groupId: string) => void;
  causes: Cause[];
  addCause: (cause: Cause) => void;
  updateCause: (cause: Cause) => void;
  deleteCause: (causeId: string) => void;
  sermons: Sermon[];
  addSermon: (sermon: Sermon) => Promise<void>;
  updateSermon: (sermon: Sermon) => Promise<void>;
  deleteSermon: (sermonId: string) => Promise<void>;
  lang: string;
  setLang: (lang: string) => void;
  messages: Message[];
  sendMessage: (receiverId: string, content: string) => void;
  markAsRead: (senderId: string) => void;
  isLoading: boolean;
  t: (key: string) => string;
}

const AppContext = createContext<AppState | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useAppContext must be used within AppProvider");
  return context;
};

// --- Helper Functions ---
const parseTimestamp = (dateString: string | number | undefined): number => {
    if (!dateString) return 0;
    if (typeof dateString === 'number') return dateString;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 0 : date.getTime();
};

// --- Helper Components ---

const ChurchWrapper = () => {
    const { churchId } = useParams<{ churchId: string }>();
    if (!churchId) return <Navigate to="/" />;
    return <ChurchPage churchId={churchId} />;
};

const LandingPage = () => {
    const { login, t, setLang, lang } = useAppContext();
    
    // Auth Modal State
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError(null);

        // Extended Timeout to 60 seconds
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Request timed out (60s). The server is taking too long to respond.")), 60000)
        );

        try {
            if (authMode === 'signup') {
                const { data, error } = await Promise.race([
                    supabase.auth.signUp({
                        email,
                        password,
                        options: { 
                            emailRedirectTo: window.location.origin,
                            data: { name: email.split('@')[0], language: lang } 
                        }
                    }),
                    timeoutPromise
                ]) as any;

                if (error) throw error;

                // Check if session was created immediately (email confirmation disabled)
                if (data?.session) {
                    setShowAuthModal(false);
                    setAuthLoading(false);
                    return;
                }

                alert(t('auth_check_email'));
                setShowAuthModal(false);
                setAuthLoading(false);
            } else {
                const { data, error } = await Promise.race([
                    supabase.auth.signInWithPassword({
                        email,
                        password
                    }),
                    timeoutPromise
                ]) as any;

                if (error) throw error;
                if (!data.user) throw new Error("Login failed - No user data returned");
                
                // Login successful - The onAuthStateChange hook in App will handle the redirect
            }
        } catch (err: any) {
            console.error("Auth error:", err);
            let message = err.message || "An unexpected error occurred.";
            
            // Map common errors to user-friendly messages
            if (message.includes("Invalid login credentials")) {
                message = "Incorrect email or password. Please try again.";
            } else if (message.includes("Email not confirmed")) {
                message = "Please confirm your email address before signing in.";
            }

            setAuthError(message);
            setAuthLoading(false); 
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col font-sans text-gray-900 relative">
            <nav className="flex justify-between items-center p-6 max-w-7xl mx-auto w-full relative z-50">
                <div className="flex items-center gap-2 font-bold text-2xl text-blue-600 tracking-tight">
                    <ChurchIcon className="w-8 h-8"/> Holyness
                </div>
                <div className="flex gap-4 items-center">
                    <div className="relative group">
                        <button className="flex items-center gap-1 text-gray-600 hover:text-blue-600 font-medium py-2">
                            <Globe className="w-4 h-4" /> {lang.toUpperCase()}
                        </button>
                        {/* Wrapper with padding-top to bridge the gap between button and menu */}
                        <div className="absolute right-0 top-full pt-2 w-32 hidden group-hover:block z-50">
                             <div className="bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 py-1 border border-gray-100">
                                <button onClick={() => setLang('en')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">English</button>
                                <button onClick={() => setLang('es')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Español</button>
                                <button onClick={() => setLang('pt')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Português</button>
                                <button onClick={() => setLang('fr')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Français</button>
                             </div>
                        </div>
                    </div>
                    <Link to="/church/register" className="text-gray-600 hover:text-blue-600 font-medium hidden sm:block">{t('for_churches')}</Link>
                    <button 
                        onClick={() => setShowAuthModal(true)} 
                        className="bg-blue-600 text-white px-6 py-2 rounded-full font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-200"
                    >
                        {t('auth_sign_in')} / {t('auth_sign_up')}
                    </button>
                </div>
            </nav>

            <main className="flex-1 flex flex-col items-center justify-center p-6 text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-blue-50 to-white -z-10" />
                <div className="absolute top-20 left-10 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" />
                <div className="absolute top-20 right-10 w-64 h-64 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" />
                <div className="absolute -bottom-8 left-20 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" />

                <div className="max-w-4xl z-10 space-y-8">
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-gray-900 leading-tight">
                        {t('landing_title_1')} <span className="text-blue-600">{t('landing_title_2')}</span>.<br/>
                        {t('landing_title_3')} <span className="text-blue-600">{t('landing_title_4')}</span>.
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        {t('landing_subtitle')}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                         <button onClick={() => setShowAuthModal(true)} className="px-8 py-4 bg-blue-600 text-white text-lg font-bold rounded-xl hover:bg-blue-700 transition shadow-xl flex items-center justify-center gap-2">
                            {t('get_started')} <Check className="w-5 h-5" />
                         </button>
                         <Link to="/church/register" className="px-8 py-4 bg-white text-gray-700 border border-gray-200 text-lg font-bold rounded-xl hover:bg-gray-50 transition shadow-sm">
                            {t('register_ministry')}
                         </Link>
                    </div>
                </div>

                <div className="mt-20 max-w-2xl mx-auto bg-white/60 backdrop-blur-sm p-8 rounded-2xl border border-white/50 shadow-sm">
                    <Quote className="w-8 h-8 text-blue-300 mx-auto mb-4" />
                    <p className="text-xl font-serif text-gray-800 italic mb-4">"{t('landing_verse_text')}"</p>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-widest">— {t('landing_verse_ref')}</p>
                </div>
            </main>

            <footer className="py-6 text-center text-gray-400 text-sm">
                &copy; {new Date().getFullYear()} Holyness. {t('landing_copyright')}
            </footer>

            {showAuthModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-8 relative max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setShowAuthModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X /></button>
                        
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">{authMode === 'signin' ? t('auth_sign_in') : t('auth_sign_up')}</h2>
                        </div>

                        {authError && <div className="mb-4 text-red-600 text-sm bg-red-50 p-3 rounded-lg flex items-center gap-2 border border-red-100"><AlertTriangle className="w-4 h-4 flex-shrink-0"/> {authError}</div>}

                        <form onSubmit={handleEmailAuth} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth_email')}</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <input 
                                        type="email" 
                                        required 
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth_password')}</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <input 
                                        type="password" 
                                        required 
                                        minLength={6}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                disabled={authLoading}
                                className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition disabled:opacity-50"
                            >
                                {authLoading ? t('processing') : authMode === 'signin' ? t('auth_sign_in') : t('auth_sign_up')}
                            </button>
                        </form>

                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                            <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">{t('auth_or')}</span></div>
                        </div>

                        <button 
                            onClick={login} 
                            className="w-full flex items-center justify-center gap-2 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition text-gray-700 font-medium"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                            Google
                        </button>
                        
                        <div className="mt-4 text-center">
                            <button 
                                onClick={() => {
                                    setAuthMode(authMode === 'signin' ? 'signup' : 'signin');
                                    setAuthError(null);
                                }}
                                className="text-sm text-blue-600 hover:underline"
                            >
                                {authMode === 'signin' ? t('auth_no_account') : t('auth_have_account')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Layout: React.FC = () => {
  const { currentUser, logout, incomingRequests, messages, t } = useAppContext();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
              setIsUserMenuOpen(false);
          }
          if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
              setIsNotificationsOpen(false);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Unread messages count
  const unreadCount = messages ? messages.filter(m => m.receiverId === currentUser?.id && !m.isRead).length : 0;

  const handleSearch = (e: React.FormEvent) => {
      e.preventDefault();
      if(searchTerm.trim()) {
          navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
          setSearchTerm('');
      }
  };

  if (!currentUser) {
      return (
          <Routes>
              <Route path="/church/register" element={<ChurchRegistration />} />
              <Route path="*" element={<LandingPage />} />
          </Routes>
      );
  }

  // Navigation Items - Centered Main Nav
  const navItems = [
      { icon: Home, label: t('nav_home'), path: '/' },
      { icon: Globe, label: t('nav_my_church'), path: currentUser.churchId ? `/church/${currentUser.churchId}` : '/search?q=church' },
      { icon: Calendar, label: t('nav_events'), path: '/events' },
      { icon: Heart, label: t('nav_volunteer'), path: '/volunteering' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
       {/* Top Navigation Bar */}
       <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
           <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="flex justify-between h-16 items-center">
                   {/* Left: Logo & Search */}
                   <div className="flex items-center gap-4 flex-shrink-0">
                       <Link to="/" className="flex items-center gap-2 font-bold text-xl text-blue-600">
                           <ChurchIcon className="w-8 h-8"/> 
                           <span className="hidden md:block">Holyness</span>
                       </Link>
                       <form onSubmit={handleSearch} className="relative ml-2">
                           <input 
                                type="text" 
                                placeholder={t('search_placeholder')}
                                className="pl-9 pr-4 py-2 bg-gray-100 border-none rounded-full text-sm focus:ring-2 focus:ring-blue-100 focus:bg-white w-40 lg:w-64 transition-all"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                           />
                           <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                       </form>
                   </div>

                   {/* Center: Main Nav Links */}
                   <nav className="hidden md:flex space-x-1 lg:space-x-8">
                       {navItems.map((item) => (
                           <Link 
                               key={item.path} 
                               to={item.path}
                               className={`flex flex-col items-center justify-center px-3 pt-1 border-b-2 text-xs font-medium transition-colors ${
                                   location.pathname === item.path 
                                   ? 'border-gray-900 text-gray-900' 
                                   : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                               }`}
                           >
                               <item.icon className="w-6 h-6 mb-1" />
                               <span className="hidden lg:block">{item.label}</span>
                           </Link>
                       ))}
                   </nav>

                   {/* Right: Actions & Profile */}
                   <div className="flex items-center gap-2 sm:gap-5">
                       <Link to="/chat" className="relative text-gray-500 hover:text-gray-700 p-1 flex flex-col items-center">
                           <MessageCircle className="w-6 h-6" />
                           {unreadCount > 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">{unreadCount}</span>}
                           <span className="text-[10px] hidden lg:block">{t('nav_messaging')}</span>
                       </Link>
                       
                       {/* Notifications */}
                       <div className="relative" ref={notifRef}>
                           <button 
                                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                                className="text-gray-500 hover:text-gray-700 p-1 flex flex-col items-center focus:outline-none"
                           >
                               <Bell className="w-6 h-6" />
                               {incomingRequests.length > 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">{incomingRequests.length}</span>}
                               <span className="text-[10px] hidden lg:block">{t('nav_notifications')}</span>
                           </button>
                           
                           {isNotificationsOpen && (
                               <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 py-2 z-50">
                                   <div className="px-4 py-2 border-b border-gray-100">
                                       <h3 className="text-sm font-bold text-gray-900">{t('nav_notifications')}</h3>
                                   </div>
                                   {incomingRequests.length > 0 ? (
                                       <div className="max-h-64 overflow-y-auto">
                                           {incomingRequests.map(id => (
                                               <div key={id} className="px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                                                   <p className="text-sm text-gray-800 mb-1">New connection request</p>
                                                   <Link to="/profile" onClick={() => setIsNotificationsOpen(false)} className="text-xs text-blue-600 font-bold hover:underline">
                                                       View Request
                                                   </Link>
                                               </div>
                                           ))}
                                       </div>
                                   ) : (
                                       <div className="px-4 py-6 text-center text-gray-500 text-sm">
                                           No new notifications
                                       </div>
                                   )}
                               </div>
                           )}
                       </div>

                       {/* User Dropdown */}
                       <div className="relative ml-2" ref={userMenuRef}>
                           <button 
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                className="flex flex-col items-center focus:outline-none"
                           >
                               <img src={currentUser.avatarUrl} className="w-7 h-7 rounded-full bg-gray-200 object-cover" alt="Profile" />
                               <span className="text-[10px] text-gray-500 hidden lg:block flex items-center gap-0.5">Me <span className="text-[8px]">▼</span></span>
                           </button>

                           {isUserMenuOpen && (
                               <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 py-1 z-50">
                                   <div className="px-4 py-3 border-b border-gray-100">
                                       <p className="text-sm font-bold text-gray-900 truncate">{currentUser.name}</p>
                                       <p className="text-xs text-gray-500 truncate">{currentUser.voluntaryCall || 'Member'}</p>
                                   </div>
                                   
                                   <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsUserMenuOpen(false)}>{t('nav_profile')}</Link>
                                   
                                   {currentUser.role !== UserRole.MEMBER && (
                                       <>
                                           <div className="border-t border-gray-100 my-1"></div>
                                           <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Management</p>
                                           {currentUser.role === UserRole.CHURCH_REP && (
                                               <Link to="/church-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsUserMenuOpen(false)}>Church Dashboard</Link>
                                           )}
                                           {(currentUser.role === UserRole.PASTOR || currentUser.role === UserRole.ADMIN) && (
                                               <>
                                                   <Link to="/sermon-builder" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsUserMenuOpen(false)}>Sermon Builder</Link>
                                                   <Link to="/admin/users" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsUserMenuOpen(false)}>Admin Tools</Link>
                                               </>
                                           )}
                                       </>
                                   )}

                                   <div className="border-t border-gray-100 my-1"></div>
                                   <Link to="/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsUserMenuOpen(false)}>{t('nav_settings')}</Link>
                                   <Link to="/guide" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsUserMenuOpen(false)}>{t('nav_guide')}</Link>
                                   <Link to="/contact" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsUserMenuOpen(false)}>{t('contact_title')}</Link>
                                   <button 
                                       type="button"
                                       onClick={(e) => { e.preventDefault(); setIsUserMenuOpen(false); logout(); }}
                                       className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                   >
                                       {t('nav_sign_out')}
                                   </button>
                               </div>
                           )}
                       </div>
                   </div>
               </div>
           </div>
       </header>

       {/* Mobile Bottom Nav */}
       <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 px-6 py-2 flex justify-between items-center">
            {navItems.map((item) => (
                <Link key={item.path} to={item.path} className={`flex flex-col items-center p-2 ${location.pathname === item.path ? 'text-gray-900' : 'text-gray-500'}`}>
                    <item.icon className={`w-6 h-6 ${location.pathname === item.path ? 'fill-current' : ''}`} />
                    <span className="text-[10px] mt-1">{item.label}</span>
                </Link>
            ))}
            <button 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} 
                className="flex flex-col items-center p-2 text-gray-500"
            >
                <Menu className="w-6 h-6" />
                <span className="text-[10px] mt-1">More</span>
            </button>
       </div>

       {/* Main Content Area */}
       <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 mb-16 md:mb-0">
            <Routes>
                <Route path="/" element={<Feed />} />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/church/:churchId" element={<ChurchWrapper />} />
                <Route path="/sermon-builder" element={<SermonBuilder />} />
                <Route path="/volunteering" element={<Volunteering />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/user/:userId" element={<Profile />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/admin/users" element={<UserManagement />} />
                <Route path="/church-dashboard" element={<ChurchDashboard />} />
                <Route path="/guide" element={<UserGuide />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
       </main>
    </div>
  );
};

const App: React.FC = () => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [churches, setChurches] = useState<Church[]>([]);
    const [posts, setPosts] = useState<Post[]>([]);
    const [events, setEvents] = useState<ChurchEvent[]>([]);
    const [prayerGroups, setPrayerGroups] = useState<PrayerGroup[]>([]);
    const [causes, setCauses] = useState<Cause[]>([]);
    const [sermons, setSermons] = useState<Sermon[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [incomingRequests, setIncomingRequests] = useState<string[]>([]);
    const [outgoingRequests, setOutgoingRequests] = useState<string[]>([]);
    const [joinedEventIds, setJoinedEventIds] = useState<string[]>([]);
    
    // Initialize language from localStorage or default to 'en'
    const [lang, setLangState] = useState(() => localStorage.getItem('holyness_language') || 'en');
    const [isLoading, setIsLoading] = useState(true);

    // Wrapper to persist language selection
    const setLang = (newLang: string) => {
        setLangState(newLang);
        localStorage.setItem('holyness_language', newLang);
    };
  
    useEffect(() => {
      // Auth change listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (session?.user) {
              try {
                  // Fetch Profile
                  const { data: profile, error } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
                  
                  if (profile) {
                      const user: User = {
                          id: profile.id,
                          name: profile.name,
                          email: profile.email,
                          avatarUrl: profile.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=random`,
                          role: profile.role as UserRole,
                          churchId: profile.church_id,
                          voluntaryCall: profile.voluntary_call,
                          favoriteBiblePassage: profile.favorite_bible_passage,
                          location: profile.location || 'Unknown',
                          language: profile.language || 'en',
                          bio: profile.bio,
                          bannerUrl: profile.banner_url,
                          connections: []
                      };
                      setCurrentUser(user);
                      
                      // If user has a specific language set in profile, prioritize it over localStorage
                      if (user.language) {
                          setLang(user.language);
                      }
                      
                      // Fetch Global Data after Profile is loaded
                      await fetchData(user.id);
                  } else {
                      // Fallback: Use basic session data if profile fetch/create failed due to DB error
                      // This ensures the user isn't stuck on "Processing..."
                      console.warn("Profile fetch failed, using fallback user object.");
                      const fallbackUser: User = {
                          id: session.user.id,
                          name: session.user.user_metadata.name || session.user.email?.split('@')[0] || 'Member',
                          email: session.user.email || '',
                          avatarUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(session.user.email || 'U')}&background=random`,
                          role: UserRole.MEMBER,
                          churchId: undefined,
                          voluntaryCall: 'Member',
                          favoriteBiblePassage: '',
                          location: '',
                          language: 'en',
                          bio: '',
                          connections: []
                      };
                      setCurrentUser(fallbackUser);
                      
                      // Attempt to create profile in background
                      await supabase.from('profiles').upsert({
                          id: session.user.id,
                          email: session.user.email,
                          name: fallbackUser.name,
                          role: 'MEMBER',
                          avatar_url: fallbackUser.avatarUrl
                      });
                  }
              } catch (e) {
                  console.error("Auth init error:", e);
              }
          } else {
              setCurrentUser(null);
          }
          setIsLoading(false);
      });
  
      return () => subscription.unsubscribe();
    }, []);

    const fetchData = async (currentUserId: string) => {
        try {
            // Use Promise.allSettled to ensure that if one fetch fails, the others still load
            const [
                profilesResult,
                churchesResult,
                connectionsResult,
                postsResult,
                eventsResult,
                groupsResult,
                causesResult,
                sermonsResult,
                messagesResult,
                attendeesResult,
                allAttendeesResult
            ] = await Promise.allSettled([
                supabase.from('profiles').select('*'),
                supabase.from('churches').select('*'),
                supabase.from('connections').select('*'),
                supabase.from('posts').select(`*, comments (id, author_id, content, created_at)`).order('created_at', { ascending: false }),
                supabase.from('events').select('*'),
                supabase.from('prayer_groups').select('*, prayer_group_members(user_id)'),
                supabase.from('causes').select('*').order('created_at', { ascending: false }),
                supabase.from('sermons').select('*').order('date', { ascending: false }),
                supabase.from('messages').select('*').or(`sender_id.eq.${currentUserId},receiver_id.eq.${currentUserId}`).order('created_at', { ascending: true }),
                supabase.from('event_attendees').select('event_id').eq('user_id', currentUserId),
                supabase.from('event_attendees').select('event_id') // Fetch all attendance records to count
            ]);

            // Process Connections First (Needed for User Mapping)
            let connectionMap: Record<string, string[]> = {};
            let incoming: string[] = [];
            let outgoing: string[] = [];

            if (connectionsResult.status === 'fulfilled' && connectionsResult.value.data) {
                connectionsResult.value.data.forEach((c: any) => {
                    if (c.status === 'ACCEPTED') {
                        if (!connectionMap[c.user_a]) connectionMap[c.user_a] = [];
                        if (!connectionMap[c.user_b]) connectionMap[c.user_b] = [];
                        connectionMap[c.user_a].push(c.user_b);
                        connectionMap[c.user_b].push(c.user_a);
                    } else if (c.status === 'PENDING') {
                        if (c.user_b === currentUserId) incoming.push(c.user_a);
                        if (c.user_a === currentUserId) outgoing.push(c.user_b);
                    }
                });
                setIncomingRequests(incoming);
                setOutgoingRequests(outgoing);
            }

            // Process Joined Events
            if (attendeesResult.status === 'fulfilled' && attendeesResult.value.data) {
                setJoinedEventIds(attendeesResult.value.data.map((a: any) => a.event_id));
            }

            // Process Event Counts
            const eventCounts: Record<string, number> = {};
            if (allAttendeesResult.status === 'fulfilled' && allAttendeesResult.value.data) {
                allAttendeesResult.value.data.forEach((a: any) => {
                    eventCounts[a.event_id] = (eventCounts[a.event_id] || 0) + 1;
                });
            }

            // Process Users
            let users: User[] = [];
            if (profilesResult.status === 'fulfilled' && profilesResult.value.data) {
                users = profilesResult.value.data.map((u: any) => ({
                    id: u.id,
                    name: u.name,
                    email: u.email,
                    avatarUrl: u.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name)}&background=random`,
                    role: u.role as UserRole,
                    churchId: u.church_id,
                    voluntaryCall: u.voluntary_call,
                    location: u.location || '',
                    bio: u.bio,
                    bannerUrl: u.banner_url,
                    favoriteBiblePassage: u.favorite_bible_passage,
                    connections: connectionMap[u.id] || []
                }));
                setAllUsers(users);
                
                // Update Current User with latest connection data
                const me = users.find(u => u.id === currentUserId);
                if (me) setCurrentUser(prev => prev ? { ...prev, connections: me.connections } : me);
            }

            // Process Churches
            if (churchesResult.status === 'fulfilled' && churchesResult.value.data) {
                const churchMemberCounts: Record<string, number> = {};
                users.forEach(u => {
                    if (u.churchId) churchMemberCounts[u.churchId] = (churchMemberCounts[u.churchId] || 0) + 1;
                });

                setChurches(churchesResult.value.data.map((c: any) => ({
                    id: c.id,
                    name: c.name,
                    description: c.description,
                    logoUrl: c.logo_url,
                    bannerUrl: c.banner_url,
                    website: c.website,
                    address: c.address,
                    country: c.country,
                    region: c.region,
                    memberCount: churchMemberCounts[c.id] || 0,
                    stripeConnected: c.stripe_connected,
                    admins: [],
                    worshipSongs: c.worship_songs || []
                })));
            }

            // Process Posts
            if (postsResult.status === 'fulfilled' && postsResult.value.data) {
                setPosts(postsResult.value.data.map((p: any) => ({
                    id: p.id,
                    authorId: p.author_id,
                    churchId: p.church_id,
                    content: p.content,
                    image_url: p.image_url,
                    video_url: p.video_url,
                    bibleCitation: p.bible_citation_ref ? { reference: p.bible_citation_ref, text: p.bible_citation_text } : undefined,
                    timestamp: parseTimestamp(p.created_at), // Safe timestamp parsing
                    likes: p.likes || [],
                    comments: p.comments ? p.comments.map((c: any) => ({
                        id: c.id,
                        authorId: c.author_id,
                        content: c.content,
                        timestamp: parseTimestamp(c.created_at)
                    })) : []
                })));
            }

            // Process Events
            if (eventsResult.status === 'fulfilled' && eventsResult.value.data) {
                setEvents(eventsResult.value.data.map((e: any) => ({
                    id: e.id,
                    churchId: e.church_id,
                    title: e.title,
                    description: e.description,
                    date: e.date,
                    location: e.location,
                    image_url: e.image_url,
                    guestSpeakers: e.guest_speakers || [],
                    attendeeCount: eventCounts[e.id] || 0, // Use calculated count
                    price: e.price
                })));
            }

            // Process Groups
            if (groupsResult.status === 'fulfilled' && groupsResult.value.data) {
                setPrayerGroups(groupsResult.value.data.map((g: any) => ({
                    id: g.id,
                    churchId: g.church_id,
                    name: g.name,
                    description: g.description,
                    meeting_schedule: g.meeting_schedule,
                    leader_id: g.leader_id,
                    members: g.prayer_group_members ? g.prayer_group_members.map((m: any) => m.user_id) : []
                })));
            }

            // Process Causes
            if (causesResult.status === 'fulfilled' && causesResult.value.data) {
                setCauses(causesResult.value.data.map((c: any) => ({
                    id: c.id,
                    title: c.title,
                    description: c.description,
                    type: c.type,
                    urgency: c.urgency,
                    contactInfo: c.contact_info,
                    authorId: c.author_id,
                    churchId: c.church_id,
                    category: c.category || 'REQUEST'
                })));
            }

            // Process Sermons
            if (sermonsResult.status === 'fulfilled' && sermonsResult.value.data) {
                setSermons(sermonsResult.value.data.map((s: any) => ({
                    id: s.id,
                    churchId: s.church_id,
                    title: s.title,
                    preacher: s.preacher,
                    date: s.date,
                    description: s.description,
                    video_url: s.video_url
                })));
            }

            // Process Messages
            if (messagesResult.status === 'fulfilled' && messagesResult.value.data) {
                setMessages(messagesResult.value.data.map((m: any) => ({
                    id: m.id,
                    senderId: m.sender_id,
                    receiverId: m.receiver_id,
                    content: m.content,
                    timestamp: parseTimestamp(m.created_at),
                    isRead: m.is_read
                })));
            }

        } catch (e) {
            console.error("Critical Data Fetch Error:", e);
        }
    };
  
    // AppState Actions (Wrappers around Supabase)
    const updateUser = async (user: User) => {
        const { error } = await supabase.from('profiles').update({
            name: user.name,
            voluntary_call: user.voluntaryCall,
            location: user.location,
            bio: user.bio,
            favorite_bible_passage: user.favoriteBiblePassage,
            church_id: user.churchId || null,
            language: user.language,
            avatar_url: user.avatarUrl,
            banner_url: user.bannerUrl
        }).eq('id', user.id);

        if (!error) {
            setCurrentUser(user);
            setAllUsers(prev => prev.map(u => u.id === user.id ? user : u));
        } else {
            console.error("Update failed:", error);
            alert("Failed to save profile changes.");
        }
    };
  
    const updateUserRole = async (userId: string, newRole: UserRole) => {
        setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
        await supabase.from('profiles').update({ role: newRole }).eq('id', userId);
    };
  
    const deleteUser = async (userId: string) => {
        setAllUsers(prev => prev.filter(u => u.id !== userId));
        await supabase.from('profiles').delete().eq('id', userId);
    };
  
    const sendConnectionRequest = async (id: string) => {
        if (!currentUser) return;
        setOutgoingRequests(prev => [...prev, id]);
        await supabase.from('connections').insert({ user_a: currentUser.id, user_b: id, status: 'PENDING' });
    };

    const acceptConnectionRequest = async (id: string) => {
        if (!currentUser) return;
        setIncomingRequests(prev => prev.filter(req => req !== id));
        await supabase.from('connections').update({ status: 'ACCEPTED' }).eq('user_a', id).eq('user_b', currentUser.id);
        if(currentUser) fetchData(currentUser.id);
    };

    const removeConnection = async (id: string) => {
        if (!currentUser) return;
        setIncomingRequests(prev => prev.filter(req => req !== id));
        setOutgoingRequests(prev => prev.filter(req => req !== id));
        await supabase.from('connections').delete().or(`and(user_a.eq.${currentUser.id},user_b.eq.${id}),and(user_a.eq.${id},user_b.eq.${currentUser.id})`);
        if(currentUser) fetchData(currentUser.id);
    };

    const toggleConnection = (id: string) => {
        if(!currentUser) return;
        if(currentUser.connections.includes(id)) removeConnection(id);
        else if(outgoingRequests.includes(id)) removeConnection(id);
        else if(incomingRequests.includes(id)) acceptConnectionRequest(id);
        else sendConnectionRequest(id);
    };
  
    const login = async () => {
        const redirectUrl = window.location.origin;
        console.log("Attempting Google Login with redirect to:", redirectUrl);
        
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: { 
                    redirectTo: redirectUrl,
                    skipBrowserRedirect: false // Force redirect flow
                }
            });
            
            if (error) {
                console.error("Supabase Auth Error:", error);
                alert("Login Failed: " + error.message);
            } else {
                console.log("OAuth initiated", data);
            }
        } catch (e: any) {
            console.error("Login Exception:", e);
            alert("An error occurred during login: " + e.message);
        }
    };
    
    const logout = async () => { 
        setCurrentUser(null);
        await supabase.auth.signOut(); 
    };
    
    const switchRole = (role: UserRole) => { if(currentUser) setCurrentUser({...currentUser, role}); };
  
    const updateChurch = async (church: Church) => {
        setChurches(prev => prev.map(c => c.id === church.id ? church : c));
        const { error } = await supabase.from('churches').update({
            name: church.name,
            description: church.description,
            website: church.website,
            address: church.address,
            logo_url: church.logoUrl,
            banner_url: church.bannerUrl,
            worship_songs: church.worshipSongs
        }).eq('id', church.id);

        if (error) {
            console.error("Error updating church:", error);
            throw error;
        }
    };
  
    const addPost = async (post: Post) => {
        setPosts([post, ...posts]);
        await supabase.from('posts').insert({
            author_id: post.authorId,
            church_id: post.churchId || null,
            content: post.content,
            image_url: post.imageUrl,
            video_url: post.videoUrl,
            bible_citation_ref: post.bibleCitation?.reference,
            bible_citation_text: post.bibleCitation?.text,
            likes: []
        });
    };

    const removePost = async (id: string) => {
        setPosts(posts.filter(p => p.id !== id));
        await supabase.from('posts').delete().eq('id', id);
    };

    const toggleLike = async (id: string) => {
        if(!currentUser) return;
        const post = posts.find(p => p.id === id);
        if (!post) return;
        
        const isLiked = post.likes.includes(currentUser.id);
        const newLikes = isLiked ? post.likes.filter(l => l !== currentUser.id) : [...post.likes, currentUser.id];
        
        setPosts(prev => prev.map(p => p.id === id ? { ...p, likes: newLikes } : p));
        await supabase.from('posts').update({ likes: newLikes }).eq('id', id);
    };

    const addComment = async (postId: string, content: string) => {
        if(!currentUser) return;
        const comment: Comment = { id: Date.now().toString(), authorId: currentUser.id, content, timestamp: Date.now() };
        setPosts(posts.map(p => p.id === postId ? { ...p, comments: [...p.comments, comment] } : p));
        await supabase.from('comments').insert({ post_id: postId, author_id: currentUser.id, content });
    };
  
    const addEvent = async (event: ChurchEvent) => {
        setEvents([...events, event]);
        await supabase.from('events').insert({
            church_id: event.churchId,
            title: event.title,
            description: event.description,
            date: event.date,
            location: event.location,
            image_url: event.imageUrl,
            guest_speakers: event.guestSpeakers,
            price: event.price
        });
    };

    const updateEvent = async (event: ChurchEvent) => {
        setEvents(events.map(e => e.id === event.id ? event : e));
        await supabase.from('events').update({
            title: event.title,
            description: event.description,
            date: event.date,
            location: event.location,
            image_url: event.imageUrl,
            guest_speakers: event.guestSpeakers,
            price: event.price
        }).eq('id', event.id);
    };

    const deleteEvent = async (id: string) => {
        setEvents(events.filter(e => e.id !== id));
        await supabase.from('events').delete().eq('id', id);
    };

    const registerForEvent = async (eventId: string): Promise<{ success: boolean; message: string }> => {
        if(!currentUser) return { success: false, message: "Must be logged in" };
        
        if (joinedEventIds.includes(eventId)) {
            return { success: false, message: "You are already registered for this event." };
        }

        try {
            const { error } = await supabase.from('event_attendees').insert({
                event_id: eventId,
                user_id: currentUser.id,
                status: 'GOING'
            });
            
            if (error) {
                if (error.code === '23505') {
                    setJoinedEventIds(prev => [...prev, eventId]);
                    return { success: false, message: "You are already registered." };
                }
                console.error("Registration error:", error);
                return { success: false, message: "Registration failed. Please try again." };
            } else {
                setJoinedEventIds(prev => [...prev, eventId]);
                setEvents(prev => prev.map(e => e.id === eventId ? { ...e, attendeeCount: e.attendeeCount + 1 } : e));
                return { success: true, message: "Successfully registered! We look forward to seeing you." };
            }
        } catch (e: any) {
            console.error("Registration exception:", e);
            return { success: false, message: e.message || "An error occurred." };
        }
    };
  
    const addPrayerGroup = async (group: PrayerGroup) => {
        setPrayerGroups([...prayerGroups, group]);
        await supabase.from('prayer_groups').insert({
            church_id: group.churchId,
            name: group.name,
            description: group.description,
            meeting_schedule: group.meetingSchedule,
            leader_id: group.leaderId
        });
    };

    const joinPrayerGroup = async (id: string) => {
        if(!currentUser) return;
        setPrayerGroups(groups => groups.map(g => g.id === id ? { ...g, members: [...g.members, currentUser.id] } : g));
        await supabase.from('prayer_group_members').insert({ group_id: id, user_id: currentUser.id });
    };

    const leavePrayerGroup = async (id: string) => {
        if(!currentUser) return;
        setPrayerGroups(groups => groups.map(g => g.id === id ? { ...g, members: g.members.filter(m => m !== currentUser.id) } : g));
        await supabase.from('prayer_group_members').delete().eq('group_id', id).eq('user_id', currentUser.id);
    };
  
    const addCause = async (cause: Cause) => {
        setCauses([...causes, cause]);
        await supabase.from('causes').insert({
            author_id: cause.authorId,
            title: cause.title,
            description: cause.description,
            type: cause.type,
            urgency: cause.urgency,
            contact_info: cause.contactInfo,
            church_id: cause.churchId,
            category: cause.category
        });
    };

    const updateCause = async (cause: Cause) => {
        setCauses(causes.map(c => c.id === cause.id ? cause : c));
        await supabase.from('causes').update({
            title: cause.title,
            description: cause.description,
            type: cause.type,
            urgency: cause.urgency,
            contact_info: cause.contactInfo,
            category: cause.category
        }).eq('id', cause.id);
    };

    const deleteCause = async (id: string) => {
        setCauses(causes.filter(c => c.id !== id));
        await supabase.from('causes').delete().eq('id', id);
    };
  
    const addSermon = async (sermon: Sermon) => {
        setSermons([...sermons, sermon]);
        await supabase.from('sermons').insert({
            church_id: sermon.churchId,
            title: sermon.title,
            preacher: sermon.preacher,
            date: sermon.date,
            description: sermon.description,
            video_url: sermon.videoUrl
        });
    };
    
    const updateSermon = async (sermon: Sermon) => {
        setSermons(sermons.map(s => s.id === sermon.id ? sermon : s));
        await supabase.from('sermons').update({
            title: sermon.title,
            preacher: sermon.preacher,
            date: sermon.date,
            description: sermon.description,
            video_url: sermon.videoUrl
        }).eq('id', sermon.id);
    };

    const deleteSermon = async (id: string) => {
        setSermons(sermons.filter(s => s.id !== id));
        await supabase.from('sermons').delete().eq('id', id);
    };
  
    const sendMessage = async (receiverId: string, content: string) => {
        if(!currentUser) return;
        const msg: Message = { id: Date.now().toString(), senderId: currentUser.id, receiverId, content, timestamp: Date.now(), isRead: false };
        setMessages([...messages, msg]);
        await supabase.from('messages').insert({ sender_id: currentUser.id, receiver_id: receiverId, content });
    };

    const markAsRead = async (senderId: string) => {
        if(!currentUser) return;
        setMessages(msgs => msgs.map(m => m.senderId === senderId && m.receiverId === currentUser.id ? { ...m, isRead: true } : m));
        await supabase.from('messages').update({ is_read: true }).eq('sender_id', senderId).eq('receiver_id', currentUser.id);
    };

    // Translation helper
    const t = (key: string) => {
        return translations[lang]?.[key] || translations['en'][key] || key;
    };
  
    const value: AppState = {
        currentUser, allUsers, updateUser, updateUserRole, deleteUser,
        incomingRequests, outgoingRequests, sendConnectionRequest, acceptConnectionRequest, removeConnection, toggleConnection,
        login, logout, switchRole,
        churches, updateChurch,
        posts, addPost, removePost, toggleLike, addComment,
        events, addEvent, updateEvent, deleteEvent, registerForEvent, joinedEventIds,
        prayerGroups, addPrayerGroup, joinPrayerGroup, leavePrayerGroup,
        causes, addCause, updateCause, deleteCause,
        sermons, addSermon, updateSermon, deleteSermon,
        lang, setLang,
        messages, sendMessage, markAsRead,
        isLoading,
        t
    };
  
    return (
        <AppContext.Provider value={value}>
            <SEO 
                title={t('seo_title')}
                description={t('seo_description')}
            />
            <HashRouter>
                <Layout />
            </HashRouter>
        </AppContext.Provider>
    );
};

export default App;